import {MutationTree} from 'vuex'
import {SupportModuleState} from '@/modules/support/store/index'
import Category from '@/modules/support/model/Category'
import _ from 'lodash'
import Vue from 'vue'
import Pagination, {PaginationInput} from '@/modules/core/model/pagination'
import Guide from '@/modules/support/model/Guide'
import Step from "@/modules/support/model/Step"
import GuideView from "@/modules/support/model/GuideView"
import {Screen} from "@/modules/support/model/Screen";

export enum MutationTypes {
    SET_ROOT_CATEGORY = 'SET_ROOT_CATEGORY',
    SET_CURRENT_CATEGORY = 'SET_CURRENT_CATEGORY',
    SET_REPLACEMENT_CATEGORY = 'SET_REPLACEMENT_CATEGORY',
    UPDATE_CATEGORY_FIELD = 'UPDATE_CATEGORY_FIELD',
    ADD_CATEGORY = 'ADD_CATEGORY',
    REMOVE_CATEGORY = 'REMOVE_CATEGORY',
    SET_GUIDE_PAGINATION = 'SET_GUIDE_PAGINATION',
    UPDATE_PAGINATION_INPUT_PAGE = 'UPDATE_PAGINATION_INPUT',
    UPDATE_PAGINATION_INPUT_FILTER = 'UPDATE_PAGINATION_INPUT_FILTER',
    UPDATE_GUIDE_PAGINATION_INPUT_OPTIONS = 'UPDATE_GUIDE_PAGINATION_INPUT_OPTIONS',
    SET_CURRENT_GUIDE = 'SET_CURRENT_GUIDE',
    SET_CURRENT_STEP = 'SET_CURRENT_STEP',
    UPDATE_STEP_FIELD = 'UPDATE_STEP_FIELD',
    REMOVE_STEP = 'REMOVE_STEP',
    UP_STEP = 'UP_STEP',
    DOWN_STEP = 'DOWN_STEP',
    UPDATE_GUIDE_FIELD = 'UPDATE_GUIDE_FIELD',
    SET_GUIDE_VIEW_PAGINATION = 'SET_GUIDE_VIEW_PAGINATION',
    UPDATE_PAGINATION_VIEW_INPUT_FILTER = 'UPDATE_PAGINATION_VIEW_INPUT_FILTER',
    UPDATE_PAGINATION_VIEW_INPUT_OPTIONS = 'UPDATE_PAGINATION_VIEW_INPUT_OPTIONS',
    SET_SCREEN_LIST = 'SET_SCREEN_LIST',
}

const mutations: MutationTree<SupportModuleState> = {
    /**
     * Establece el valor de la categoría raíz actual
     *
     * @param state
     * @param category
     */
    [MutationTypes.SET_ROOT_CATEGORY]: (state, category: Category) => state.rootCategory = category,

    /**
     * Establece la categoría actualmente en edición por el usuario
     *
     * @param state
     * @param category
     */
    [MutationTypes.SET_CURRENT_CATEGORY]: (state, category: Category) => {
        state.currentCategory = _.cloneDeep(category)
    },

    /**
     * Actualiza un campo de la categoría actualmente seleccionad por el usuario
     *
     * @param state
     * @param field
     * @param value
     */
    [MutationTypes.UPDATE_CATEGORY_FIELD]: (state, {field, value}) => {
        if (!state.currentCategory) {
            return
        }

        Vue.set(state.currentCategory, field, value)
    },
    /**
     * Añade una categoría al árbol
     * @param state
     * @param category
     */
    [MutationTypes.ADD_CATEGORY]: (state, category: Category) => {
        const root = state.rootCategory
        root.removeCategory(category.id)
        const parent = root.findCategory(category.parent)
        if (!parent) {
            throw new Error('No se ha encontrado el padre para la nueva categoría')
        }

        parent.children.push(category)
    },
    /**
     * Elimina una categoría del árbol
     *
     * @param state
     * @param id
     */
    [MutationTypes.REMOVE_CATEGORY]: (state, id: number) => {
        state.rootCategory.removeCategory(id)
    },
    /**
     * Establece una paginación de guías
     *
     * @param state
     * @param pagination
     */
    [MutationTypes.SET_GUIDE_PAGINATION]: (state, pagination: Pagination<Guide>) => {
        state.guidePagination = pagination
    },
    /**
     * Actualiza la página de las opciones de paginación
     *
     * @param state
     * @param page
     */
    [MutationTypes.UPDATE_PAGINATION_INPUT_PAGE]: (state, page: number) => {
        state.guidePaginationInput.page = page
    },
    /**
     * Actualiza un filtro de las opciones de paginación
     *
     * @param state
     * @param page
     */
    [MutationTypes.UPDATE_PAGINATION_INPUT_FILTER]: (state, { field, value }) => {
        Vue.set(state.guidePaginationInput.filters || {}, field, value)
    },
    /**
     * Establece la guía actualmente selecciona por el usuario
     * @param state
     * @param guide
     */
    [MutationTypes.SET_CURRENT_GUIDE]: (state, guide: Guide) => {
        state.currentGuide = guide
    },
    /**
     * Establece el paso actualmente en edición
     *
     * @param state
     * @param step
     */
    [MutationTypes.SET_CURRENT_STEP]: (state, step: Step) => {
        state.currentStep = step
    },
    /**
     * Actualiza uno de los campos del paso actualmente en edición
     *
     * @param state
     * @param step
     */
    [MutationTypes.UPDATE_STEP_FIELD]: (state, {field, value}) => {
        Vue.set(state.currentStep, field, value)
    },
    /**
     * Elimina un paso de la guía actualmente en edición
     *
     * @param state
     * @param step
     */
    [MutationTypes.REMOVE_STEP]: (state, step: Step) => {
        state.currentGuide.removeStep(step)
    },
    [MutationTypes.UP_STEP]: (state, {step, positions}) => {
        state.currentGuide.moveUp(step, positions)
    },
    [MutationTypes.DOWN_STEP]: (state, {step, positions}) => {
        state.currentGuide.moveDown(step, positions)
    },
    /**
     * Actualiza un campo de la guía actualmente en edición
     *
     * @param state
     * @param field
     * @param value
     */
    [MutationTypes.UPDATE_GUIDE_FIELD]: (state, {field, value}) => {
        Vue.set(state.currentGuide, field, value)
    },
    /**
     * Establece la paginación de vistas de guías actualmente mostrada el usuario
     *
     * @param state
     * @param pagination
     */
    [MutationTypes.SET_GUIDE_VIEW_PAGINATION]: (state, pagination: Pagination<GuideView>) => {
        state.guideViewPagination = pagination
    },
    /**
     * Actualiza un filtro de las opciones de paginación de visitas a guías
     *
     * @param state
     * @param page
     */
    [MutationTypes.UPDATE_PAGINATION_VIEW_INPUT_FILTER]: (state, { field, value }) => {
        Vue.set(state.guideViewPaginationInput.filters || {}, field, value)
    },
    /**
     * Actualiza las opciones del input de la paginación
     *
     * @param state
     * @param field
     * @param value
     */
    [MutationTypes.UPDATE_PAGINATION_VIEW_INPUT_OPTIONS]: (state, input: PaginationInput) => {
        Object.assign(state.guideViewPaginationInput, input)
    },
    /**
     * Actualiza las opciones del input de la paginación de guías
     *
     * @param state
     * @param input
     */
    [MutationTypes.UPDATE_GUIDE_PAGINATION_INPUT_OPTIONS]: (state, input: PaginationInput) => {
        Object.assign(state.guidePaginationInput, input)
    },
    /**
     * Establece la categoría de reemplazo para las guías de la categoría que esta siendo borrada
     *
     * @param state
     * @param category
     */
    [MutationTypes.SET_REPLACEMENT_CATEGORY]: (state, category: Category) => {
        state.replacementCategory = category
    },
    /**
     * Establece la lista de pantallas
     *
     * @param state
     * @param screens
     */
    [MutationTypes.SET_SCREEN_LIST]: (state, screens: Screen[]) => {
        state.screens = screens
    },
}

export default mutations

